/**
 * Rounds: live rounds (`/rounds`, status not 1/4) and settled rounds (`/rounds-final`, status 1/4)
 * share one allow-list (`rounds` in module-fields.config.ts), so they share the filter mapping.
 *
 * Traps handled here (rgs-api-contract, source):
 * - amounts arrive already divided by 100 (`"12.34"`); they're shifted back to cents for MoneyCell;
 * - the detail spreads the whole Game relation, engine API key included: the schema keeps only the
 *   fields the screen needs, so nothing else reaches component state;
 * - `gameId`, `operatorId` and `Currency` aren't filterable: games and operators filter by name;
 * - the allow-list names relation paths (`Game.Name`, `Player.Brand`…) as sortable, but TypeORM
 *   refuses a sort on one (400 `Property "Game.Name" was not found in "RoundsFinal"`), so only
 *   the round's own columns sort;
 * - PATCH takes `Status` only (no reason field); export covers live rounds only.
 */
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { download, http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { bigintString, decimalString, flag, jsonColumn } from '@/api/zod-helpers';
import { nextDay } from '@/components/data-table';
import { majorToCents } from '@/lib/money-units';
import { filterParam, listSearchSchema, toPagedQuery } from '@/lib/url-state';

// ---- statuses ---------------------------------------------------------------------------------

/** ROUND_STATUSES in rgs-database. */
export const ROUND_STATUS = {
  STARTED: 0,
  SETTLED: 1,
  FINISHED: 2,
  VOID: 3,
  VOID_FINAL: 4,
  WAITING_SUB_GAMES: 5,
} as const;

export const ROUND_STATUS_LABELS: Record<number, string> = {
  0: 'Started',
  1: 'Settled',
  2: 'Finished',
  3: 'Void',
  4: 'Void, settled',
  5: 'Waiting for sub-games',
};

export const statusLabel = (status: number) => ROUND_STATUS_LABELS[status] ?? `Status ${status}`;

/** Settled rounds live in rounds_final; everything else is still in rounds. */
export const SETTLED_STATUSES = [ROUND_STATUS.SETTLED, ROUND_STATUS.VOID_FINAL] as const;
export const LIVE_STATUSES = [
  ROUND_STATUS.STARTED,
  ROUND_STATUS.FINISHED,
  ROUND_STATUS.VOID,
  ROUND_STATUS.WAITING_SUB_GAMES,
] as const;

/** rounds/sessions `Channel` (rgs-domain). */
export const channelLabel = (channel: number | null | undefined) =>
  channel === 1
    ? 'Desktop'
    : channel === 2
      ? 'Mobile'
      : channel === null || channel === undefined
        ? '—'
        : `Channel ${channel}`;

// ---- schemas ----------------------------------------------------------------------------------

/** Money in major units from the API → integer cents (null when absent). */
const money = z
  .union([z.string(), z.number()])
  .nullish()
  .transform((v) => majorToCents(v ?? null));

/** Timestamps: an ISO string, or `{}` where the API serialises a Date badly. */
const timestamp = z.unknown().transform((v) => (typeof v === 'string' && v ? v : null));

const text = z
  .string()
  .nullish()
  .transform((v) => v ?? null);
const count = z.coerce
  .number()
  .nullish()
  .transform((v) => v ?? null);

export const roundSchema = z.object({
  Id: bigintString,
  UUID: text,
  playerId: z.coerce.number(),
  gameId: z.coerce.number(),
  IsFreeSpin: flag,
  BetAmount: money,
  WinAmount: money,
  BonusWinAmount: money,
  JackpotWinAmount: money,
  Status: z.coerce.number(),
  Currency: z.string(),
  HasBonusGames: flag,
  Channel: count,
  RetryCount: count,
  CreatedAt: timestamp,
  Game: z.object({ Name: text }).nullish(),
  Operator: z.object({ Name: text }).nullish(),
  Player: z.object({ OperatorPlayerNickname: text, Brand: text }).nullish(),
});

export type Round = z.infer<typeof roundSchema>;

export const roundDetailSchema = roundSchema.extend({
  operatorId: count,
  TransactionType: count,
  StatusNote: jsonColumn(z.unknown()).optional(),
  ExternalTransactionId: text,
  ExternalBetTransactionId: text,
  ExternalWinTransactionId: text,
  CampaignId: text.or(count),
  AwardId: text,
  AwardAdditionalData: jsonColumn(z.unknown()).optional(),
  GameEngineData: jsonColumn(z.unknown()).optional(),
  BonusGamesEngineResults: jsonColumn(z.unknown()).optional(),
  ExchangeRateToBaseCurrency: decimalString.nullish(),
  FairnessId: text.or(count),
  FairnessNonceId: count,
  UpdatedAt: timestamp,
  Settled: z.boolean().optional(),
  ReplayUrl: text,
  // Only what the screen shows. The API sends the whole game row, engine API key included.
  Game: z.object({ Id: count, Name: text, RTP: text }).nullish(),
  Operator: z.object({ Name: text, UUID: text }).nullish(),
  Player: z
    .object({
      Id: count,
      OperatorPlayerId: text,
      OperatorPlayerNickname: text,
      Brand: text,
      Currency: text,
      IsTestPlayer: flag.optional(),
    })
    .nullish(),
});

export type RoundDetail = z.infer<typeof roundDetailSchema>;

// ---- list search ------------------------------------------------------------------------------

/** The allow-list's own columns: its relation paths answer 400 (see the top of this file). */
export const ROUND_SORT_FIELDS = [
  'Id',
  'Status',
  'playerId',
  'WinAmount',
  'BetAmount',
  'BonusWinAmount',
  'RetryCount',
  'CreatedAt',
  'UpdatedAt',
] as const;

/** The server defaults to `playerId desc`; a rounds list reads newest first. */
export const DEFAULT_ROUND_SORT: SortDescriptor = { field: 'CreatedAt', dir: 'desc' };

export const roundsSearchSchema = listSearchSchema({
  sortFields: ROUND_SORT_FIELDS,
  filters: {
    status: filterParam.number(),
    game: filterParam.string(),
    operator: filterParam.string(),
    brand: filterParam.string(),
    player: filterParam.number(),
    from: filterParam.date(),
    to: filterParam.date(),
  },
});

export type RoundsSearch = z.infer<typeof roundsSearchSchema>;

const UUID_TEXT = /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i;

/** Quick search: digits = round id, a UUID = round uuid, anything else = player nickname. */
export function roundsPagedQuery(search: RoundsSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  const q = search.q?.trim();
  if (q) {
    if (/^\d+$/.test(q)) filters.push({ field: 'Id', operator: 'eq', value: q });
    else if (UUID_TEXT.test(q))
      filters.push({ field: 'UUID', operator: 'eq', value: q.replace(/-/g, '').toLowerCase() });
    else filters.push({ field: 'Player.OperatorPlayerNickname', operator: 'contains', value: q });
  }
  if (search.status !== undefined) filters.push({ field: 'Status', operator: 'eq', value: search.status });
  if (search.game) filters.push({ field: 'Game.Name', operator: 'eq', value: search.game });
  if (search.operator) filters.push({ field: 'Operator.Name', operator: 'eq', value: search.operator });
  if (search.brand) filters.push({ field: 'Player.Brand', operator: 'eq', value: search.brand });
  if (search.player !== undefined) filters.push({ field: 'playerId', operator: 'eq', value: search.player });
  if (search.from) filters.push({ field: 'CreatedAt', operator: 'gte', value: search.from });
  if (search.to) filters.push({ field: 'CreatedAt', operator: 'lt', value: nextDay(search.to) });
  return toPagedQuery(search, { defaultSort: DEFAULT_ROUND_SORT, filters });
}

const listSchema = pagedSchema(roundSchema);

export type RoundsView = 'settled' | 'live';

const VIEW_PATH: Record<RoundsView, string> = { settled: '/rounds-final', live: '/rounds' };

export const roundsKeys = {
  all: ['rounds'] as const,
  list: (view: RoundsView, query: PagedQuery) => ['rounds', 'list', view, query] as const,
  detail: (id: string) => ['rounds', 'detail', id] as const,
  lookups: (what: string) => ['rounds', 'lookup', what] as const,
};

const LIVE_INTERVAL_MS = 10_000;

export const roundsListQuery = (view: RoundsView, search: RoundsSearch, options: { poll?: boolean } = {}) => {
  const query = roundsPagedQuery(search);
  return queryOptions({
    queryKey: roundsKeys.list(view, query),
    queryFn: ({ signal }) => http(VIEW_PATH[view], { query: encodePagedQuery(query), signal }, listSchema),
    placeholderData: keepPreviousData,
    // Live list: every 10 s while the tab is visible (TanStack pauses hidden tabs), backing off on errors.
    refetchInterval: options.poll ? (q) => Math.min(LIVE_INTERVAL_MS * 2 ** q.state.fetchFailureCount, 120_000) : false,
    refetchIntervalInBackground: false,
  });
};

export const roundQuery = (id: string) =>
  queryOptions({
    queryKey: roundsKeys.detail(id),
    queryFn: ({ signal }) => http(`/rounds/${encodeURIComponent(id)}`, { signal }, roundDetailSchema),
  });

// ---- filter options ---------------------------------------------------------------------------

const nameListSchema = pagedSchema(z.object({ Id: z.coerce.number(), Name: z.string() }));
const byName = JSON.stringify({ field: 'Name', dir: 'asc' });

/** Game names for the filter (the list filters by name: `gameId` isn't in the allow-list). */
export const gameNamesQuery = () =>
  queryOptions({
    queryKey: roundsKeys.lookups('games'),
    queryFn: ({ signal }) => http('/games', { query: { skip: 0, take: 1000, sort: byName }, signal }, nameListSchema),
    staleTime: 5 * 60_000,
  });

export const operatorNamesQuery = () =>
  queryOptions({
    queryKey: roundsKeys.lookups('operators'),
    queryFn: ({ signal }) =>
      http('/operators', { query: { skip: 0, take: 1000, sort: byName }, signal }, nameListSchema),
    staleTime: 5 * 60_000,
  });

// ---- writes -----------------------------------------------------------------------------------

/** Status only: no money moves, and the API has no field for a reason yet. */
export function useUpdateRoundStatus(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: number) =>
      http(`/rounds/${encodeURIComponent(id)}`, { method: 'PATCH', body: { Status: status } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roundsKeys.all });
    },
  });
}

/** XLSX of live rounds matching the filters (the export DTO takes sort and filter only). */
export async function exportLiveRounds(search: RoundsSearch): Promise<void> {
  const { sort, filter } = encodePagedQuery(roundsPagedQuery(search));
  const { blob, filename } = await download('/rounds/export', { query: { sort, filter } });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? 'rounds.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}
